import type { FormikErrors } from 'formik'

import type { CustomField } from '@sales-center/common'

import type { ICustomFieldValueFormModel } from '@user/components/CustomFields/Editable/typings'
import type { IAutoReviewMissingField } from '@user/store/draftLeads/typings'

/**
 * Form values of the "Update Required Fields" modal for a Pending Lead. Flat keys mirror
 * IPendingLeadAutoReviewInput; empty strings and null mean "not provided". Only the fields the
 * server reports as missing are rendered and sent.
 */
export interface UpdateRequiredFieldsFormModel
  extends ICustomFieldValueFormModel {
  firstName: string
  lastName: string
  company: string
  phoneNumber: string
  emailAddress: string
  branchId: string
  salesTeamId: number | null
  assigneeId: string
  salesFunnelId: number | null
  salesFunnelStageId: number | null
  divisionId: string
  altPhoneNumber: string
  street1: string
  street2: string
  city: string
  stateAbbreviation: string
  postalCode: string
  locationTypeId: string
  note: string
  targetPestIds: string[]
  campaignId: string
  sourceId: string
  referralCode: string
  referringBillTo: string
}

/** Server keys of the system-required fields (PendingLeadAutoReviewFieldChecker). */
export type SystemMissingKey =
  | 'nameOrCompany'
  | 'phoneOrEmail'
  | 'branchId'
  | 'salesTeamId'
  | 'assigneeId'
  | 'salesFunnelId'
  | 'salesFunnelStageId'

/** Server keys of the built-in Additional Fields the modal can render. */
export type AdditionalMissingKey =
  | 'divisionId'
  | 'altPhoneNumber'
  | 'address'
  | 'locationTypeId'
  | 'note'
  | 'targetPests'
  | 'campaignId'
  | 'leadSourceId'
  | 'referral'

/**
 * One render unit of the modal, produced by groupMissingFields. `assignment` bundles whichever of
 * team, owner, funnel and stage are missing because the cascade needs them together. `unknown`
 * keeps any key this client does not recognise so it is never dropped; the modal shows a
 * "Use Review" note for those.
 */
export type MissingFieldGroup =
  | { kind: 'nameOrCompany'; field: IAutoReviewMissingField }
  | { kind: 'phoneOrEmail'; field: IAutoReviewMissingField }
  | { kind: 'branchId'; field: IAutoReviewMissingField }
  | { kind: 'assignment'; fields: IAutoReviewMissingField[] }
  | {
      kind: 'additional'
      key: AdditionalMissingKey
      field: IAutoReviewMissingField
    }
  | { kind: 'custom'; fieldId: number; field: IAutoReviewMissingField }
  | { kind: 'unknown'; field: IAutoReviewMissingField }

/** A selectable option whose name must travel with its id (branch, division, ...). */
export interface NamedOption {
  id: string
  name: string
}

/** Option lists buildAutoReviewInput resolves the *Id + name pairs from. */
export interface AutoReviewLookups {
  branches: NamedOption[]
  divisions: NamedOption[]
  locationTypes: NamedOption[]
  campaigns: NamedOption[]
  sources: NamedOption[]
  customFields: CustomField[]
}

/** Group-level error key set when the name OR-group is unsatisfied (as Add Lead's nameGroup). */
export const NAME_GROUP_ERROR_KEY = 'nameGroup'

/** Group-level error key set when neither phone nor email is provided. */
export const CONTACT_GROUP_ERROR_KEY = 'contactGroup'

/** Inputs that Task 4 field components mark red when NAME_GROUP_ERROR_KEY is set. */
export const NAME_GROUP_FIELDS = ['firstName', 'lastName', 'company'] as const

/** Inputs that Task 4 field components mark red when CONTACT_GROUP_ERROR_KEY is set. */
export const CONTACT_GROUP_FIELDS = ['phoneNumber', 'emailAddress'] as const

/**
 * Errors of the modal: per-field errors under the form model keys, plus the group-level
 * messages NAME_GROUP_ERROR_KEY and CONTACT_GROUP_ERROR_KEY.
 */
export type UpdateRequiredFieldsErrors =
  FormikErrors<UpdateRequiredFieldsFormModel> & {
    nameGroup?: string
    contactGroup?: string
  }
